import { create } from '@bufbuild/protobuf';
import useCloudService from '@grpc/composables/useCloudService';
import type { Flavour } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import { type Project } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import { type Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import { ListProjectFlavoursRequestSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import type { ListProjectFlavoursResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import { PaginationRequestSchema } from 'gpcore-grpc/gpcore/type/v1/pagination_pb';
import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';

export interface FlavourStoreState {
  flavours: Array<Flavour>;
}

export interface FlavourRequest {
  datacenter: Datacenter;
  project: Project;
  includeSoldOut?: boolean;
  page: number;
}

export const useFlavourStore = defineStore('flavour', () => {
  const cloudService = useCloudService();

  const state = reactive<FlavourStoreState>({
    flavours: new Array<Flavour>(),
  });

  const reloadAllFlavours = async (req: FlavourRequest): Promise<ListProjectFlavoursResponse> => {
    const request = create(ListProjectFlavoursRequestSchema);
    request.id = req.project.id;
    request.datacenterId = req.datacenter.id;
    request.includeSoldOut = req.includeSoldOut || false;

    const { response, error } = await cloudService.executeApiCall('listProjectFlavours', request);
    if (error != null) throw error;
    state.flavours = response.flavours;

    return response;
  };

  const reloadFlavours = async (req: FlavourRequest): Promise<ListProjectFlavoursResponse> => {
    const request = create(ListProjectFlavoursRequestSchema);
    const pagination = create(PaginationRequestSchema);
    pagination.page = req.page;
    request.pagination = pagination;
    request.id = req.project.id;
    request.datacenterId = req.datacenter.id;
    request.includeSoldOut = req.includeSoldOut || false;

    const { response, error } = await cloudService.executeApiCall('listProjectFlavours', request);
    if (error != null) throw error;
    state.flavours = response.flavours;

    return response;
  };

  const reloadFlavoursByIDs = async (
    datacenterId: string,
    projectId: string,
    includeSoldOut: boolean,
  ): Promise<ListProjectFlavoursResponse> => {
    const request = create(ListProjectFlavoursRequestSchema);
    request.id = projectId;
    request.datacenterId = datacenterId;
    request.includeSoldOut = includeSoldOut;

    const { response, error } = await cloudService.executeApiCall('listProjectFlavours', request);
    if (error != null) throw error;

    state.flavours = response.flavours;

    return response;
  };

  return {
    ...toRefs(state as FlavourStoreState),
    reloadAllFlavours,
    reloadFlavours,
    reloadFlavoursByIDs,
  };
});
